import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuditLog } from 'src/app/shared/models/AuditLog';
import { DemandStatusService } from 'src/app/shared/services/demand-status.service';

interface FieldChange {
  field: string;
  label: string;
  before: any;
  after: any;
}

interface HistoryItem {
  log: AuditLog;
  date: Date;
  projet?: string;
  statutAvant?: string;
  statutApres?: string;
  motif?: string;
  changes: FieldChange[];
}

@Component({
  selector: 'app-demand-history',
  templateUrl: './demand-history.component.html',
  styleUrls: ['./demand-history.component.less']
})
export class DemandHistoryComponent {

  isLoading = true;
  items: HistoryItem[] = [];
  demandNumber = '';

  // ── Labels lisibles pour tous les champs de Demand ───────────────────────
  // null = masqué
  private fieldLabels: Record<string, string | null> = {
    // Champs principaux
    state:                              'Statut',
    cost:                               'Montant (€)',
    load:                               'Charge (j)',
    reference:                          'N° Bon de commande',
    description:                        'Désignation',
    serviceType:                        'Service',
    prestationType:                     'Type de prestation',
    facturationMode:                    'Mode de facturation',
    reductionCost:                      'Réduction',
    deliveryDate:                       'Date de livraison réelle',
    revisedDeliveryDate:                'Date de livraison revue',
    realizationDateTime:                'Date de réalisation (GO)',
    estimateQuoteDate:                  'Date prévisionnelle devis',
    estimateProductionDelivryDate:      'Livraison production prévisionnelle',
    acceptanceDeliveryDate:             'Date validation recette',
    acceptanceValidationDeliveryDate:   'Date validation recette (confirmée)',
    realAcceptanceDeliveryDate:         'Livraison recette réelle',
    realIntegrationDeliveryDate:        'Livraison intégration DSI réelle',
    realQualifDeliveryDate:             'Livraison qualification DSI réelle',
    realDeliveryFactoryDate:            'Sortie usine réelle',
    productionDate:                     'Livraison production réelle',
    validateEstimationCostDate:         'Date validation chiffrage',
    applicationName:                    'Application',
    demandNumber:                       'N° Demande',
    demandType:                         'Type de demande',
    emissionQuote:                      'Émission du devis',
    year:                               'Année',
    jiraReference:                      'Référence Jira',
    galileiProjectCode:                 'Code projet Galéi',
    galileiProjectDescription:          'Libellé projet Galéi',
    galileiProjectActivityCode:         'Code activité Galéi',
    galileiProjectActivityDescription:  'Libellé activité Galéi',

    // Champs techniques → masqués
    id:                  null,
    version:             null,
    updatedAt:           null,
    createdAt:           null,
    creationDateTime:    null,
    userRecorder:        null,
    userResponsable:     null,
    project:             null,
    pack:                null,
    orderForm:           null,
    functionalReference: null,
    comments:            null,
  };

  // Libellés des statuts d'échéance (EcheanceStatus enum ordinal)
  private echeanceStatusLabels: Record<string, string> = {
    '0': 'Par défaut',
    '1': 'En attente validation CDS',
    '2': 'En attente validation SNCF',
    '3': 'Validé SNCF',
    '4': 'Refusé',
    // Noms string si le backend envoie le nom de l'enum
    'DEFAULT':                    'Par défaut',
    'EN_ATTENTE_VALIDATION_CDS':  'En attente validation CDS',
    'EN_ATTENTE_VALIDATION_SNCF': 'En attente validation SNCF',
    'VALIDE_SNCF':                'Validé SNCF',
    'REFUSE':                     'Refusé',
  };

  // Libellés des statuts demande
  private demandStatusLabels: Record<string, string> = {
    CHIFFRE:         'Chiffré',
    ENGAGE:          'Engagé',
    ENGAGE_APPROUVE: 'Engagé / Approuvé',
    EN_ATTENTE:      'En attente',
    LIVRE:           'Livré',
    FERME:           'Fermé',
    ANNULE:          'Annulé',
  };

  // Mois en français
  private moisFr = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  private hiddenFields = ['id', 'version', 'updatedAt', 'createdAt',
    'creationDateTime', 'userRecorder', 'userResponsable', 'project',
    'pack', 'orderForm', 'functionalReference', 'comments'];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { demandNumber: string },
    public dialogRef: MatDialogRef<DemandHistoryComponent>,
    private demandStatusService: DemandStatusService
  ) {
    this.demandNumber = data.demandNumber;
    this.loadHistory();
  }

  loadHistory(): void {
    this.demandStatusService.getAuditHistory('DEMAND', this.demandNumber).subscribe({
      next: (logs: AuditLog[]) => {
        this.items = logs.map(log => this.toHistoryItem(log));
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private parseJson(s: any): any {
    if (!s) return null;
    if (typeof s === 'string') {
      try { return JSON.parse(s); } catch { return null; }
    }
    return s;
  }

  private toHistoryItem(log: AuditLog): HistoryItem {
    const metadata  = this.parseJson(log.metadata) ?? {};
    const changedRaw = this.parseJson(log.changedFields);

    let allChanges: { field: string; before: any; after: any }[] = [];

    if (Array.isArray(changedRaw)) {
      allChanges = changedRaw.filter(c => c?.field);
    } else if (changedRaw && typeof changedRaw === 'object') {
      const before = this.parseJson(log.snapshotBefore) ?? {};
      const after  = this.parseJson(log.snapshotAfter)  ?? {};
      allChanges = Object.keys(changedRaw).map(f => ({
        field:  f,
        before: before?.[f],
        after:  after?.[f]
      }));
    }

    // Statut extrait séparément
    const stateChange = allChanges.find(c => c.field === 'state');

    const changes: FieldChange[] = allChanges
      .filter(c => c.field !== 'state' && !this.hiddenFields.includes(c.field))
      .map(c => {
        const label = this.humanizeField(c.field);
        if (label === null) return null;
        return {
          field:  c.field,
          label,
          before: this.formatValue(c.field, c.before),
          after:  this.formatValue(c.field, c.after),
        };
      })
      .filter((c): c is FieldChange => c !== null);

    return {
      log,
      date:        new Date(log.createdAt),
      projet:      (log as any).projet,
      statutAvant: stateChange
        ? (this.demandStatusLabels[stateChange.before] ?? stateChange.before)
        : (metadata?.statutAvant ?? undefined),
      statutApres: stateChange
        ? (this.demandStatusLabels[stateChange.after] ?? stateChange.after)
        : (metadata?.statutApres ?? undefined),
      motif:  metadata?.motifRetour ?? metadata?.motifRefus,
      changes,
    };
  }

  /**
   * Humanise un nom de champ technique.
   * Gère les échéances avec leur mois : echeances[id=xxx].status → "Échéance Juin 2026 — Statut"
   */
  private humanizeField(field: string): string | null {
    // Champ direct connu
    if (Object.prototype.hasOwnProperty.call(this.fieldLabels, field)) {
      return this.fieldLabels[field] as string | null;
    }

    // Échéances avec id : echeances[id=xxx].status
    // On essaie d'extraire la date depuis le snapshot pour humaniser
    const echeanceIdMatch = field.match(/^echeances\[id=([^\]]+)\]\.(.+)$/);
    if (echeanceIdMatch) {
      const subField = echeanceIdMatch[2];
      const subLabel = this.echeanceSubFieldLabel(subField);
      if (subLabel === null) return null;
      // Pas de date disponible sans le snapshot → label générique
      return `Échéance — ${subLabel}`;
    }

    // Échéances par index : echeances[0].status
    const echeanceIdxMatch = field.match(/^echeances\[(\d+)\]\.(.+)$/);
    if (echeanceIdxMatch) {
      const index    = parseInt(echeanceIdxMatch[1]) + 1;
      const subField = echeanceIdxMatch[2];
      const subLabel = this.echeanceSubFieldLabel(subField);
      if (subLabel === null) return null;
      return `Échéance ${index} — ${subLabel}`;
    }

    // Champ avec point : prend la feuille
    const dotIdx = field.lastIndexOf('.');
    if (dotIdx !== -1) {
      const leaf = field.substring(dotIdx + 1);
      if (Object.prototype.hasOwnProperty.call(this.fieldLabels, leaf)) {
        return this.fieldLabels[leaf] as string | null;
      }
    }

    // Fallback visible
    return field;
  }

  private echeanceSubFieldLabel(subField: string): string | null {
    const map: Record<string, string | null> = {
      status:      'Statut',
      cost:        'Montant (€)',
      load:        'Charge (j)',
      delayedCost: 'Coût reporté',
      projected:   'Projeté',
      date:        null,  // technique
      id:          null,
    };
    return Object.prototype.hasOwnProperty.call(map, subField)
      ? map[subField]
      : subField;
  }

  /**
   * Extrait le mois/année d'une date ISO pour les libellés d'échéance.
   * Retourne ex: "Juin 2026"
   */
  private dateToMoisAn(dateStr: string): string | null {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return this.moisFr[d.getMonth()] + ' ' + d.getFullYear();
  }

  private formatValue(field: string, value: any): any {
    if (value === null || value === undefined || value === '') return null;

    // Statuts demande → libellé français
    if (field === 'state' && typeof value === 'string') {
      return this.demandStatusLabels[value] ?? value;
    }

    // Statuts échéance (ordinal ou nom)
    if (field === 'status' || field.endsWith('.status')) {
      const key = String(value);
      return this.echeanceStatusLabels[key] ?? value;
    }

    // Booléen
    if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
    if (value === 'true')  return 'Oui';
    if (value === 'false') return 'Non';

    // Montants
    if (['cost', 'delayedCost', 'reductionCost'].includes(field)) {
      const n = Number(value);
      if (!isNaN(n)) return n.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €';
    }

    // Charge
    if (field === 'load') {
      const n = Number(value);
      if (!isNaN(n)) return n + ' j';
    }

    // Dates ISO → format FR
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toLocaleDateString('fr-FR');
    }

    return value;
  }

  /**
   * Post-traitement des changements d'échéances :
   * Si le snapshot contient la date de l'échéance, on l'injecte dans le label.
   * Appelé depuis le template pour enrichir les changes déjà calculés.
   */
  enrichEcheanceLabel(change: FieldChange, log: AuditLog): string {
    if (!change.field.startsWith('echeances[')) return change.label;

    // Tente d'extraire la date depuis le snapshot after ou before
    const idMatch = change.field.match(/^echeances\[id=([^\]]+)\]/);
    if (idMatch) {
      const echeanceId = idMatch[1];
      const snapshot   = this.parseJson(log.snapshotAfter) ?? this.parseJson(log.snapshotBefore);
      const echeances: any[] = snapshot?.echeances ?? [];
      const echeance = echeances.find((e: any) => e.id === echeanceId);
      if (echeance?.date) {
        const moisAn = this.dateToMoisAn(echeance.date);
        if (moisAn) {
          const subField = change.field.split('.').pop() ?? '';
          const subLabel = this.echeanceSubFieldLabel(subField) ?? subField;
          return `Échéance ${moisAn} — ${subLabel}`;
        }
      }
    }

    return change.label;
  }

  // ── Helpers template ──────────────────────────────────────────────────────

  isStatusChange(item: HistoryItem): boolean {
    return !!item.statutApres && item.statutAvant !== item.statutApres;
  }

  operationLabel(op: string): string {
    const m: Record<string, string> = {
      CREATE: 'Création', UPDATE: 'Modification', DELETE: 'Suppression'
    };
    return m[op] ?? op;
  }

  operationIcon(op: string): string {
    const m: Record<string, string> = {
      CREATE: 'add_circle', UPDATE: 'edit', DELETE: 'delete'
    };
    return m[op] ?? 'info';
  }

  operationIconColor(op: string): string {
    const m: Record<string, string> = {
      CREATE: '#2E7D32', UPDATE: '#1565C0', DELETE: '#E64A19'
    };
    return m[op] ?? '#616161';
  }

  operationColor(op: string): string {
    return this.operationIconColor(op);
  }

  statutColor(label?: string): string {
    if (!label) return '#616161';
    try {
      const cls = this.demandStatusService.statusColor(
        this.demandStatusService.fromBackendStatut(label)
      );
      if (cls === 'state-green') return '#2E7D32';
      if (cls === 'state-red')   return '#E64A19';
    } catch {}
    return '#1565C0';
  }
}